<template>
  <div
    v-for="side of ['left', 'right'] as const"
    :key="side"
    class="SeatMapWings"
    :class="`SeatMapWings--${side}`"
    :style="{ top: `${coordinates.top}px`, width: `${WIDTH}px` }"
    aria-hidden="true"
  >
    <svg :width="WIDTH" :height="svgHeight" :viewBox="`0 0 ${WIDTH} ${svgHeight}`">
      <path :d="path" fill="currentColor" />
    </svg>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';

const props = defineProps<{
  /** Inclusive row-number range the wings visually span. */
  range: [number, number];
  /** The measured row elements, keyed by row number. */
  rows: Partial<Record<number, HTMLElement>>;
  /** The SeatMap's own root element, used as the positioning origin. */
  container?: HTMLElement | null;
}>();

// Wing shape traced from the aircraft's technical drawing — not derived from any token or
// prop, so these stay literal constants rather than being made "configurable".
const DRAWING_WIDTH = 275;
const DRAWING_APEX_HEIGHT = 175;
const DRAWING_TRAILING_EDGE_HEIGHT = 500;
const DRAWING_LEADING_EDGE_HEIGHT = 415;
const WIDTH = DRAWING_WIDTH;
const TAN_A = DRAWING_APEX_HEIGHT / DRAWING_WIDTH;
const TAN_B = (DRAWING_TRAILING_EDGE_HEIGHT - DRAWING_LEADING_EDGE_HEIGHT) / DRAWING_WIDTH;

function positionOf(element: HTMLElement | undefined, containerY: number): number {
  return (element?.getBoundingClientRect().y ?? containerY) - containerY;
}

// A computed, not a one-off onMounted measurement: SeatMap's row elements attach via function
// refs as later siblings mount, after this component's own onMounted would already have fired.
// Reading `rows[key]` here tracks each key, so this recomputes once every row is in.
const coordinates = computed(() => {
  const [start, end] = props.range;
  const containerY = props.container?.getBoundingClientRect().y ?? 0;

  return {
    top: positionOf(props.rows[start], containerY),
    bottom: positionOf(props.rows[end + 1], containerY),
  };
});

const height = computed(() => coordinates.value.bottom - coordinates.value.top);
const svgHeight = computed(() => height.value + TAN_B * WIDTH);

const path = computed(() => {
  const apexY = TAN_A * WIDTH;

  return `M${WIDTH} 0 L${WIDTH} ${height.value} L0 ${svgHeight.value} V${apexY} L${WIDTH} 0 Z`;
});
</script>

<style lang="scss">
@use '@cvo/ui/styles/design-system' as vars;

@layer cvo-ui {
  .SeatMapWings {
    position: absolute;
    z-index: -1;
    color: vars.$organisms-seatmap-wings-color;

    // Physical left/right, not logical: this traces the aircraft's actual left/right wing,
    // which doesn't mirror for RTL locales.
    &--left {
      right: 100%;
    }

    &--right {
      left: 100%;
      transform: scaleX(-1);
    }
  }
}
</style>
